import React, { useCallback, useEffect, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import AdminMailSettings from './AdminMailSettings';
import AdminMailWebhook from './AdminMailWebhook';
import './AdminNewsletter.css';

const PAGE = 50;

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// Журнал исходящих писем: каждое письмо СВОД и результат отправки.
function AdminMailLog({ onOpenWebhook }) {
    const [filters, setFilters] = useState({ status: '', kind: '', days: '', q: '', delivery: '' });
    const [query, setQuery] = useState('');
    const [data, setData] = useState(null);
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setFilters((f) => (f.q === query ? f : { ...f, q: query })), 400);
        return () => clearTimeout(t);
    }, [query]);

    const load = useCallback(async (offset = 0) => {
        setLoading(true);
        try {
            const params = { limit: PAGE, offset };
            Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
            const res = await api.get('/admin/mail-log', { params });
            setData(res.data);
            setItems((prev) => (offset ? [...prev, ...res.data.items] : res.data.items));
            setError(null);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось загрузить журнал'));
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => { load(0); }, [load]);

    const set = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
    const s = data?.summary;

    return (
        <section className="admin-main-card">
            <div className="nl-head">
                <h2 className="admin-main-card__title">Журнал писем</h2>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => load(0)} disabled={loading}>
                    {loading ? 'Обновляем…' : 'Обновить'}
                </button>
            </div>
            <div className="nl-body">
                <p className="nl-hint">
                    Все письма СВОД: коды подтверждения, сброс пароля, приглашения, отчёты и рассылки. «Отправлено» —
                    письмо принято почтовым сервисом SMTP.BZ; «Ошибка» — сервис его не принял, причина указана в строке.
                    Доставлено ли письмо в ящик, видно, если включены <button type="button" className="nl-link nl-link--inline" onClick={onOpenWebhook}>уведомления SMTP.BZ</button>.
                    Тексты писем не хранятся. Записи старше {data?.retention_days || 180} дней удаляются.
                </p>

                {s && (
                    <div className="nl-stats">
                        {[['day', 'За сутки'], ['week', 'За 7 дней'], ['month', 'За 30 дней']].map(([key, label]) => (
                            <div key={key} className="nl-stat">
                                <span>Писем {label.toLowerCase()}</span>
                                <strong>{s[key].sent + s[key].failed}</strong>
                                <em className={s[key].failed ? 'nl-error-text' : ''}>
                                    {s[key].failed ? `отправлено ${s[key].sent} · ошибок ${s[key].failed}` : 'все отправлены'}
                                </em>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mlog-filters">
                    <div className="nl-seg" role="radiogroup" aria-label="Статус">
                        {[['', 'Все'], ['sent', 'Отправлено'], ['failed', 'Ошибки']].map(([v, label]) => (
                            <button key={v || 'all'} type="button" role="radio" aria-checked={filters.status === v}
                                    className={filters.status === v ? 'is-active' : ''} onClick={() => set('status', v)}>
                                {label}
                            </button>
                        ))}
                    </div>
                    <select value={filters.kind} onChange={(e) => set('kind', e.target.value)} aria-label="Тип письма">
                        <option value="">Все типы</option>
                        {Object.entries(data?.kinds || {}).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                    </select>
                    <select value={filters.days} onChange={(e) => set('days', e.target.value)} aria-label="Период">
                        <option value="">За всё время</option>
                        <option value="1">За сутки</option>
                        <option value="7">За 7 дней</option>
                        <option value="30">За 30 дней</option>
                    </select>
                    <select value={filters.delivery} onChange={(e) => set('delivery', e.target.value)} aria-label="Судьба письма">
                        <option value="">Любая судьба</option>
                        <option value="delivered">Доставлено</option>
                        <option value="opened">Открыто</option>
                        <option value="bounced">Отказ ящика</option>
                        <option value="spam">Жалоба «Спам»</option>
                        <option value="none">Нет данных о доставке</option>
                    </select>
                    <input type="search" placeholder="Поиск по адресу или теме" value={query}
                           onChange={(e) => setQuery(e.target.value)} aria-label="Поиск" />
                </div>

                {error && <div className="admin-notice admin-notice--error">{error}</div>}

                <div className="admin-table-scroll">
                    <table className="nl-table mlog-table">
                        <thead>
                            <tr><th>Время</th><th>Кому</th><th>Тип и тема</th><th>Статус</th><th>Отправитель</th></tr>
                        </thead>
                        <tbody>
                            {data && items.length === 0 && (
                                <tr><td colSpan={5} className="nl-muted">Писем с такими условиями нет</td></tr>
                            )}
                            {items.map((r) => (
                                <tr key={r.id}>
                                    <td className="mlog-time">{formatDate(r.created_at)}</td>
                                    <td className="mlog-to">{r.to_email}</td>
                                    <td>
                                        <div className="mlog-kind">{r.kind_label}</div>
                                        <div className="nl-muted mlog-subject">{r.subject || '—'}</div>
                                    </td>
                                    <td>
                                        <span className={`nl-rstatus nl-rstatus--${r.status}`}>
                                            {r.status === 'sent' ? 'Отправлено' : 'Ошибка'}
                                        </span>
                                        {r.delivery_status && (
                                            <div>
                                                <span className={`nl-rstatus whook-status--${r.delivery_status} mlog-delivery`}
                                                      title={r.delivery_details || ''}>
                                                    {data?.delivery_labels?.[r.delivery_status] || r.delivery_status}
                                                </span>
                                            </div>
                                        )}
                                        {r.delivery_details && ['bounced', 'soft_bounce', 'spam'].includes(r.delivery_status) && (
                                            <div className="nl-error-text">{r.delivery_details}</div>
                                        )}
                                        {r.error && <div className="nl-error-text">{r.error}</div>}
                                        {r.provider_id && <div className="nl-muted mlog-id" title="Номер письма в SMTP.BZ">№ {r.provider_id}</div>}
                                    </td>
                                    <td className="nl-muted mlog-from">{r.from_email || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {data && items.length < data.total && (
                    <div className="mlog-more">
                        <button type="button" className="btn btn--secondary" onClick={() => load(items.length)} disabled={loading}>
                            Показать ещё ({data.total - items.length})
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}

// Вкладка «Почта»: настройки сервера и журнал писем.
export function AdminMailTab() {
    const [view, setView] = useState('settings');
    return (
        <>
            <div className="nl-seg mlog-switch" role="tablist" aria-label="Почта">
                {[['settings', 'Настройки'], ['log', 'Журнал писем'], ['webhook', 'Статусы доставки']].map(([v, label]) => (
                    <button key={v} type="button" role="tab" aria-selected={view === v}
                            className={view === v ? 'is-active' : ''} onClick={() => setView(v)}>
                        {label}
                    </button>
                ))}
            </div>
            {view === 'settings' && <AdminMailSettings />}
            {view === 'log' && <AdminMailLog onOpenWebhook={() => setView('webhook')} />}
            {view === 'webhook' && <AdminMailWebhook />}
        </>
    );
}

export default AdminMailLog;
